import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";

import { ContextSidebar } from "@/components/feature/layout/left-sidebar/contextSidebar";
import { RightDrawer } from "@/components/feature/layout/right-sidebar/rightDrawer";
import { PromptGeneratorContextProvider } from "@/components/feature/promptGenerator/contextProvider";
import { useCurrentPdf } from "@/views/chat/hooks/useCurrentPdf";
import { useParams } from "react-router";
import * as Sentry from "@sentry/react";
import { useGetFileDetailsQuery } from "@/api/files";
import { Item } from "@/types/fse";
import { CurrentPdfProvider } from "@/contexts/setPdfContext";
import { usePromptLibraryContext } from "@/modules/promptLibrary/promptLibrary.context";
import { ModifyNewPromptLibraryItemDialog } from "@/modules/promptLibrary/components/modifyNewPromptLibraryItemDialog";

interface ChatLayoutProps {
  children: React.ReactNode;
}

const ChatLayoutContent = ({ children }: ChatLayoutProps) => {
  const { systemId = "" } = useParams<{ systemId: string }>();
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isRightDrawerVisible, setIsRightDrawerVisible] = useState(true);
  const { setSelectedFiles, handleFileSelect, rightDrawerContent, setRightDrawerContent, setRootItem } =
    useCurrentPdf();

  React.useEffect(() => {
    setRightDrawerContent("pdf");
  }, [setRightDrawerContent]);

  const { data } = useGetFileDetailsQuery({ fse_id: systemId, depth: 200 });
  const { createPromptLibraryModalState, closeCreatePromptLibraryModal } = usePromptLibraryContext();

  useEffect(() => {
    const initializeSelectedFiles = async () => {
      try {
        if (data) {
          setRootItem(data);
          const allFileIds = new Set<string>();
          const collectFileIds = (item: Item) => {
            if (item.type === "file") {
              allFileIds.add(item.id);
            } else if (item.type === "folder") {
              Object.values(item.items).forEach(collectFileIds);
            }
          };
          collectFileIds(data);
          setSelectedFiles(Array.from(allFileIds.values()));
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    void initializeSelectedFiles();
  }, [systemId, setSelectedFiles, setRootItem, data]);

  const toggleSidebarVisibility = useCallback(() => {
    setIsSidebarVisible(prev => !prev);
  }, []);

  const toggleRightDrawerVisibility = useCallback(() => {
    setIsRightDrawerVisible(prev => !prev);
  }, []);

  return (
    <PromptGeneratorContextProvider>
      <div className="flex h-[95vh] w-full">
        <ContextSidebar
          systemId={systemId}
          onFileSelect={handleFileSelect}
          isVisible={isSidebarVisible}
          onToggleVisibility={toggleSidebarVisibility}
          showFolders={false}
          showToggleAll={true}
        />
        <motion.main
          className="flex flex-grow overflow-hidden"
          initial={false}
          animate={{
            marginLeft: 0,
          }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex-[38%] overflow-hidden" style={{ minWidth: "300px" }}>
            {children}
          </div>
          <RightDrawer
            showPdfFileName={true}
            isVisible={isRightDrawerVisible}
            toggleVisibility={toggleRightDrawerVisibility}
            content={rightDrawerContent}
            setContent={setRightDrawerContent}
          />
        </motion.main>
      </div>
      {createPromptLibraryModalState && (
        <ModifyNewPromptLibraryItemDialog
          mode="add"
          isOpen={createPromptLibraryModalState !== null}
          setIsOpen={() => closeCreatePromptLibraryModal()}
          item={{
            prompt: createPromptLibraryModalState.prompt,
            source: createPromptLibraryModalState.source,
          }}
        />
      )}
    </PromptGeneratorContextProvider>
  );
};

const ChatLayout = (props: ChatLayoutProps) => (
  <CurrentPdfProvider>
    <ChatLayoutContent {...props} />
  </CurrentPdfProvider>
);
export default ChatLayout;
